/**
 * Snapshot UI formatting helpers
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowSnapshotKind } from '@trafficpulse/shared';

export function formatSnapshotSource(source: FlowSnapshotKind): string {
  switch (source) {
    case 'manual':
      return 'Manual';
    case 'auto_apply':
      return 'Auto · Custom Apply';
    case 'auto_standard':
      return 'Auto · Standard Apply';
    case 'auto_weights':
      return 'Auto · Weights/Toggle';
    case 'auto_rotation':
      return 'Auto · Queue Rotation';
    default:
      return source;
  }
}

export function snapshotSourceHelpText(source: FlowSnapshotKind): string {
  switch (source) {
    case 'manual':
      return 'Snapshot taken manually from the Snapshot button before an edit.';
    case 'auto_apply':
      return 'Auto-snapshot taken before the Custom optimizer applied weights to Voluum.';
    case 'auto_standard':
      return 'Auto-snapshot taken before "Apply to Voluum" on the Standard tab. Captures the live flow so you can revert if the apply went wrong.';
    case 'auto_weights':
      return 'Auto-snapshot taken before a weight or toggle change was pushed to Voluum.';
    case 'auto_rotation':
      return 'Auto-snapshot taken before the queue rotation changed the flow (promoted/eliminated elements), so a rotation can be undone.';
    default:
      return 'Snapshot of the Voluum flow that can be restored with one click.';
  }
}

export function snapshotBadgeTone(source: FlowSnapshotKind): string {
  return source === 'manual'
    ? 'bg-success/10 text-success border-success/40'
    : 'bg-muted text-muted-foreground border-border';
}

/** Two-letter initials for a compact avatar (name or email). */
export function snapshotUserInitials(primaryLabel: string): string {
  const t = primaryLabel.trim();
  if (!t) return '?';
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]!.slice(0, 1)}${parts[1]!.slice(0, 1)}`.toUpperCase();
  }
  if (t.includes('@')) {
    const local = t.split('@')[0] ?? '';
    const slice = local.slice(0, 2);
    return (slice || '??').toUpperCase();
  }
  return t.slice(0, 2).toUpperCase();
}

export function snapshotActorPrimaryLabel(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  if (displayName?.trim()) return displayName.trim();
  if (email?.trim()) return email.trim();
  return 'Unknown user';
}

export function snapshotActorTooltip(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string | undefined {
  const name = displayName?.trim();
  const mail = email?.trim();
  if (name && mail && name !== mail) return `${name} · ${mail}`;
  if (name) return name;
  if (mail) return mail;
  return undefined;
}

export function formatSnapshotDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}
